import React, { Component } from 'react';
import throttle from 'lodash/throttle';
import { NewLaserBoard } from '@tencent/live-components';
import styles from './index.less';
import { TPoint } from '@tencent/live-components/src/components/LiveShow/NewLaserBoard/interface';
import { laserBoardEventsKeys, laserBoardEventsMap } from '@framework/meeting-room-slidePlayer/event-bus-instance';
import { IEventEmitter } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';

interface CusorPosition {
    X: number;
    Y: number;
}

interface Props {
    /** LaserBoard EventBus, 事件名 + 事件参数 定义见 @see EventMap */
    eventBus: IEventEmitter<laserBoardEventsMap>;

    /** 画板宽度 px */
    width: number;
    /** 画板高度 px */
    height: number;
    /** 画板缩放比例，即文档的缩放 */
    scale: number;

    /** 是否为演示者，只有演示者会广播自身轨迹 */
    isRoleMaster: boolean;
}

interface States {
    pointList: TPoint[];
}

/** 此坐标标识 cursor 为隐藏 */
const HIDDEN_CURSOR_POSITION = { X: -999, Y: -999 } as const;

class LaserBoard extends Component<Props, States> {
    public state = {
        pointList: [],
    };
    private boardRef = React.createRef<HTMLDivElement>();
    private boardRect: DOMRect;
    private cursorPosition: CusorPosition = HIDDEN_CURSOR_POSITION;
    private currentRaf = 0;

    public componentDidMount() {
        this.loopAnimation();

        window.addEventListener('resize', this.throttledResize);
        this.throttledResize();

        this.props.eventBus.on(laserBoardEventsKeys.DRAW_LASER_CUSOR, this.setCursorPosition);

        if (this.props.isRoleMaster) {
            // board 本身不能响应鼠标事件(pointer-events: none，否则点击事件无法穿透), 需要挂载在父元素上
            const parentDom = this.boardRef.current?.parentElement;
            if (parentDom) {
                parentDom.style.cursor = 'none';
                parentDom.addEventListener('mousemove', this.handleMouseMove);
                parentDom.addEventListener('mouseleave', this.handleMouseLeave);
            }
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.width !== this.props.width || prevProps.height !== this.props.height) {
            this.throttledResize();
        }
    }

    public componentWillUnmount() {
        window.cancelAnimationFrame(this.currentRaf);

        window.removeEventListener('resize', this.throttledResize);

        this.props.eventBus.off(laserBoardEventsKeys.DRAW_LASER_CUSOR, this.setCursorPosition);

        if (this.props.isRoleMaster) {
            // board 本身不能响应鼠标事件(pointer-events: none，否则点击事件无法穿透), 需要挂载在父元素上
            const parentDom = this.boardRef.current?.parentElement;
            if (parentDom) {
                parentDom.style.cursor = 'inherit';
                parentDom.removeEventListener('mousemove', this.handleMouseMove);
                parentDom.removeEventListener('mouseleave', this.handleMouseLeave);
            }
        }
    }

    public render() {
        const { width, height } = this.props;
        const { pointList } = this.state;
        const boardStyle: React.CSSProperties = {
            width: `${width}px`,
            height: `${height}px`,
            left: `calc(50% - ${width}px / 2)`,
            top: `calc(50% - ${height}px / 2)`,
        };

        return (
            <div className={styles.board} style={boardStyle} ref={this.boardRef}>
                <NewLaserBoard
                    width={width}
                    height={height}
                    pointList={pointList}
                    circleSize={10}
                />
            </div>
        );
    }

    private isCursorHidden({ X, Y }: CusorPosition) {
        return X === HIDDEN_CURSOR_POSITION.X && Y === HIDDEN_CURSOR_POSITION.Y;
    }

    private setCursorPosition = (cursorPosition: CusorPosition) => {
        this.cursorPosition = cursorPosition;
    };

    private loopAnimation = () => {
        if (this.isCursorHidden(this.cursorPosition)) {
            // cursor 隐藏时需要清空，否则 cursor 重现时轨迹会跳动；
            this.resetPointList();
        } else {
            const { scale } = this.props;
            const point = {
                X: this.cursorPosition.X * scale,
                Y: this.cursorPosition.Y * scale,
            };
            const newPointList = [point, ...this.state.pointList].slice(0, 15);
            this.setState({
                pointList: newPointList,
            });
        }

        this.currentRaf = window.requestAnimationFrame(this.loopAnimation);
    };

    private resize = () => {
        const boardDom = this.boardRef.current;
        if (!boardDom) return;

        this.boardRect = boardDom.getBoundingClientRect();
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private throttledResize = throttle(this.resize, 300);

    private resetPointList = () => {
        if (this.state.pointList.length === 0) return;
        this.setState({
            pointList: [],
        });
    };

    private handleMouseMove = (event: MouseEvent) => {
        const scale = this.props.scale || 1;
        // 换算成画布尺寸
        const position = {
            X: (event.clientX - this.boardRect.left) / scale,
            Y: (event.clientY - this.boardRect.top) / scale,
        };

        this.setCursorPosition(position);
        this.props.eventBus.emit(laserBoardEventsKeys.CUSOR_MOVE, position);
    };

    /** 鼠标离开 board 区域隐藏激光笔 */
    private handleMouseLeave = () => {
        // 不能直接使用 leave 事件的 坐标；当 leave 的位置距离面板边缘很近时，会漏出半个 cursor；
        // 因此使用固定坐标标识隐藏
        this.setCursorPosition(HIDDEN_CURSOR_POSITION);
        this.props.eventBus.emit(laserBoardEventsKeys.CUSOR_MOVE, HIDDEN_CURSOR_POSITION);
    };
}

export default LaserBoard;
